import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2 } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";

export default function AdminProjectsPage() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mentor/projects").catch(() => ({ data: [] }));
      setIdeas(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = showArchived ? ideas : ideas.filter((i) => !i.is_archived);
  const visiblePager = usePager(visible, 25);

  const archive = async (i) => {
    if (!window.confirm(`Archive "${i.title}"? It will be hidden from the active list.`)) return;
    setBusy((b) => ({ ...b, [`a_${i.id}`]: true }));
    try {
      await api.post(`/ideas/${i.id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [`a_${i.id}`]: false })); }
  };

  const unarchive = async (i) => {
    setBusy((b) => ({ ...b, [`a_${i.id}`]: true }));
    try {
      await api.post(`/ideas/${i.id}/unarchive`);
      toast.success("Unarchived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [`a_${i.id}`]: false })); }
  };

  const remove = async (i) => {
    if (!window.confirm(
      `Permanently delete "${i.title}"?\n\nAll workspace data (chat, files, tasks, collaborators) will also be removed. This cannot be undone.`
    )) return;
    setBusy((b) => ({ ...b, [`d_${i.id}`]: true }));
    try {
      await api.delete(`/admin/ideas/${i.id}`);
      toast.success("Deleted");
      setIdeas((arr) => arr.filter((x) => x.id !== i.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [`d_${i.id}`]: false })); }
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ALL PROJECTS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Platform <em className="italic text-kb-purple">projects</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">All projects currently assigned to mentors across the platform.</p>
        </div>
        <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-2">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="projects-show-archived" /> SHOW ARCHIVED
        </label>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        visible.length === 0 ? <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No projects to show.</div> :
        (
          <>
            <div className="mt-8 grid gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
              {visiblePager.pageItems.map((i) => (
                <div key={i.id} className="bg-white p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`admin-project-row-${i.id}`}>
                  <Link to={`/admin/ideas/${i.id}`} className="flex-1 min-w-0 hover:opacity-80">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{i.id} · {i.domain} · {i.user_name}</div>
                    <div className="font-serif text-lg text-kb-ink mt-1">{i.title}</div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <span className="kb-mono text-[10px] uppercase text-kb-purple bg-kb-purple/5 border border-kb-purple/15 px-2 py-1 rounded-sm whitespace-nowrap">{i.status}</span>
                    {i.is_archived && (
                      <span className="kb-mono text-[10px] uppercase text-slate-600 bg-slate-100 border border-slate-200 px-2 py-1 rounded-sm whitespace-nowrap">archived</span>
                    )}
                    {i.is_archived ? (
                      <button onClick={() => unarchive(i)} disabled={busy[`a_${i.id}`]}
                        className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary border border-slate-200 hover:border-kb-primary px-2.5 py-1 rounded-sm inline-flex items-center gap-1"
                        data-testid={`admin-project-${i.id}-unarchive-btn`}>
                        <ArchiveRestore className="w-3 h-3" /> Unarchive
                      </button>
                    ) : (
                      <button onClick={() => archive(i)} disabled={busy[`a_${i.id}`]}
                        className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted border border-slate-200 hover:text-kb-primary hover:border-kb-primary px-2.5 py-1 rounded-sm inline-flex items-center gap-1"
                        data-testid={`admin-project-${i.id}-archive-btn`}>
                        <Archive className="w-3 h-3" /> Archive
                      </button>
                    )}
                    <button onClick={() => remove(i)} disabled={busy[`d_${i.id}`]}
                      className="kb-mono text-[10px] uppercase tracking-wider text-rose-600 border border-rose-200 hover:bg-rose-50 px-2.5 py-1 rounded-sm inline-flex items-center gap-1"
                      data-testid={`admin-project-${i.id}-delete-btn`}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pager {...visiblePager} label="projects" testId="admin-projects-pager" />
          </>
        )}
    </div>
  );
}
